import { Bell, CircleHelp, Sparkles } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '~/components/ui/breadcrumb';
import { Button } from '~/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/lib/utils';

interface BreadcrumbEntry {
  label?: string;
  href?: string;
  content?: React.ReactNode;
}

interface PageContainerProps {
  children: React.ReactNode;
  title?: string;
  badge?: React.ReactNode;
  subtitle?: string;
  actions?: React.ReactNode;
  breadcrumbs?: BreadcrumbEntry[];
  className?: string;
}

export function PageContainer({
  children,
  title,
  badge,
  subtitle,
  actions,
  breadcrumbs,
  className
}: PageContainerProps): React.ReactNode {
  const { t } = useTranslation();
  const hasBreadcrumbs = breadcrumbs && breadcrumbs.length > 0;

  return (
    <div className={cn('flex h-full flex-col', className)}>
      <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-xl border-b border-border/50 bg-card px-4 py-2">
        <div>
          {hasBreadcrumbs && (
            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbs.map((item, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <BreadcrumbSeparator />}
                    {item.content ? (
                      item.content
                    ) : (
                      <BreadcrumbItem>
                        {item.href ? (
                          <BreadcrumbLink
                            href={item.href}
                            className="block max-w-[3rem] sm:max-w-[12rem] truncate"
                          >
                            {item.label}
                          </BreadcrumbLink>
                        ) : (
                          <BreadcrumbPage className="block max-w-[3rem] sm:max-w-[12rem] truncate">
                            {item.label}
                          </BreadcrumbPage>
                        )}
                      </BreadcrumbItem>
                    )}
                  </React.Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-foreground"
                />
              }
            >
              <Sparkles className="size-4" />
              <span className="hidden sm:inline">{t('topbar.whatsNew')}</span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="sm:hidden">
              {t('topbar.whatsNew')}
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="text-muted-foreground hover:text-foreground"
                  aria-label={t('topbar.help')}
                />
              }
            >
              <CircleHelp className="size-4" />
            </TooltipTrigger>
            <TooltipContent side="bottom">{t('topbar.help')}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="text-muted-foreground hover:text-foreground"
                  aria-label={t('topbar.notifications')}
                />
              }
            >
              <Bell className="size-4" />
            </TooltipTrigger>
            <TooltipContent side="bottom">{t('topbar.notifications')}</TooltipContent>
          </Tooltip>
        </div>
      </div>
      <div className="flex flex-1 min-h-0 flex-col gap-6 overflow-y-auto p-4 sm:p-8">
        {(title ?? subtitle) && (
          <div
            className={cn(
              'flex flex-col gap-1',
              actions && 'flex-row items-start justify-between gap-4'
            )}
          >
            <div className="flex min-w-0 flex-col gap-1">
              {title && (
                <div className="flex min-w-0 items-center gap-2">
                  <h1 className="min-w-0 truncate text-2xl font-semibold tracking-tight">
                    {title}
                  </h1>
                  {badge}
                </div>
              )}
              {subtitle && <p className="truncate text-sm text-muted-foreground">{subtitle}</p>}
            </div>
            {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
